// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { isWeb } from '@/client/platform';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import App from './App';
import { ThemeProvider } from './components/Layout/ThemeProvider';
import { TooltipProvider } from './components/ui/tooltip';
import { ConnectionProvider } from './context/ConnectionContext';
import { createHost, HostProvider } from './host';
import './i18n';
import { injectHost } from './store/chatStore';
import './style/index.css';

// If you want use Node.js, the`nodeIntegration` needs to be enabled in the Main process.
// import './demos/node'
const host = createHost();
injectHost(host);
const Router = isWeb() ? BrowserRouter : HashRouter;
const initialChannel = isWeb() ? 'web' : 'desktop';


// Initially inject spinner animation keyframes into document head
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `
  @keyframes eigent-pulse {
    0%, 100% { transform: scale(0.8); opacity: 0.6; }
    50% { transform: scale(1.1); opacity: 1; }
  }
`;
document.head.appendChild(spinnerStyle);

// ========================================
// Initial Loading Spinner Component
// Warm color theme (eigent orange palette)
// ========================================
const InitialLoadingSpinner = () => (
  <div
    style={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#faf7f6',
    }}
  >
    {/* Warm orange/red gradient spinner with pure CSS animation (no keyframes needed) */}
    <div
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #ff8c69 0%, #ff7043 100%)',
        boxShadow: '0 4px 20px rgba(255, 112, 67, 0.4)',
      }}
      className="eigent-spinner-pulse"
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
          animation: 'spin 1s linear infinite',
        }}
      />
    </div>
  </div>
);

// ========================================
// App Wrapper - removes initial loading spinner after mount
// ========================================
const AppWrapper = () => {
  useEffect(() => {
    // Remove the initial HTML loading spinner when React mounts
    const initLoading = document.getElementById('init-loading');
    if (initLoading) {
      initLoading.style.opacity = '0';
      setTimeout(() => initLoading.remove(), 350);
    }
  }, []);

  return <App />;
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <Suspense fallback={<InitialLoadingSpinner />}>
    <Router>
      <HostProvider host={host}>
        <ConnectionProvider channel={initialChannel}>
          <ThemeProvider>
            <TooltipProvider>
              <AppWrapper />
            </TooltipProvider>
          </ThemeProvider>
        </ConnectionProvider>
      </HostProvider>
    </Router>
  </Suspense>
);

postMessage({ payload: 'removeLoading' }, '*');
